import type { JSX, ReactElement } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FiltersPopover, Select } from '@feathr/components';
import ProjectSelect from '@feathr/extender/components/ProjectSelect';
import { AttributionModel } from '@feathr/report_components';

import { useConversionsFiltersPopoverState } from './ConversionsFiltersPopover.hooks';
import {
  activeFilterCount,
  buildCampaignTypeOptions,
  DEFAULT_FILTERS,
  type ICampaignTypeOption,
  type IFiltersState,
} from './ConversionsFiltersPopover.utils';

export interface IConversionsFiltersPopoverProps {
  appliedFilters: IFiltersState;
  onApply: (filters: IFiltersState) => void;
  /** Receives the toggle callback so the caller can wire it to onClick. */
  trigger: (toggle: () => void) => ReactElement;
}

function ConversionsFiltersPopover({
  appliedFilters,
  onApply,
  trigger,
}: Readonly<IConversionsFiltersPopoverProps>): JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { workingState, isDirty, setAttr, setProjects, setTypes, reset } =
    useConversionsFiltersPopoverState(appliedFilters);

  const showReset = activeFilterCount(appliedFilters) > 0;

  const campaignTypeOptions = useMemo(() => buildCampaignTypeOptions(t), [t]);
  const selectedCampaignTypes = campaignTypeOptions.filter(({ id }) =>
    workingState.types.includes(id),
  );

  function handleOpenChange(opened: boolean): void {
    if (!opened) {
      reset();
    }
    setIsOpen(opened);
  }

  function handleTypesChange(options: ICampaignTypeOption[]): void {
    setTypes(options.map(({ id }) => id));
  }

  function handleApply(): void {
    onApply(workingState);
    setIsOpen(false);
  }

  function handleReset(): void {
    onApply({ ...DEFAULT_FILTERS });
  }

  return (
    <FiltersPopover
      isDirty={isDirty}
      isOpen={isOpen}
      name={'conversions-filters-popover'}
      onApply={handleApply}
      onOpenChange={handleOpenChange}
      onReset={handleReset}
      showReset={showReset}
      trigger={trigger}
    >
      <AttributionModel onChange={setAttr} value={workingState.attr} />
      <ProjectSelect
        label={t('Projects')}
        onChangeMulti={setProjects}
        placeholder={t('All projects')}
        values={[...workingState.projects]}
      />
      <Select<ICampaignTypeOption>
        getOptionLabel={({ name }: ICampaignTypeOption): string => name}
        getOptionValue={({ id }: ICampaignTypeOption): string => id}
        isMulti={true}
        label={t('Campaign types')}
        menuShouldScrollIntoView={false}
        name={'campaign-types'}
        onSelectMulti={handleTypesChange}
        options={campaignTypeOptions}
        placeholder={t('All types')}
        value={selectedCampaignTypes}
      />
    </FiltersPopover>
  );
}

export default ConversionsFiltersPopover;
